import React, { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { OnboardingProvider, useOnboarding } from "@/contexts/OnboardingContext";
import { ThemeProvider } from "next-themes";
import { CookieConsent } from "@/components/common/CookieConsent";
import { AccountSetupWizard } from "@/components/onboarding/AccountSetupWizard";
import { AxisChatWidget } from "@/components/common/AxisChatWidget";
import { SnapToPriceButton } from "@/components/valuation";
import { AppUpdatePrompt, OfflineIndicator, SmartInstallPrompt } from "@/components/pwa";
import { MobileBottomNav } from "@/components/mobile/MobileBottomNav";
import { PendingPaymentProvider } from "@/components/payments/PendingPaymentProvider";
import { FeatureGate } from "@/components/FeatureGate";
import { PageLoader } from "@/components/common/PageLoader";
import { VaultPartnerLayout } from "./components/vault/VaultPartnerLayout";
import { BrandLayout } from "./components/brand/BrandLayout";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";

// Lazy-loaded page components
const Index = lazy(() => import("./pages/Index"));
const PreLaunch = lazy(() => import("./pages/PreLaunch"));
const PreLaunchCinematic = lazy(() => import("./pages/PreLaunchCinematic"));
const MarketSignal = lazy(() => import("./pages/MarketSignal"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Markets = lazy(() => import("./pages/Markets"));
const Ticker = lazy(() => import("./pages/Ticker"));
const Sell = lazy(() => import("./pages/Sell"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Vault = lazy(() => import("./pages/Vault"));
const VaultDeposits = lazy(() => import("./pages/VaultDeposits"));
const PartnerPortal = lazy(() => import("./pages/PartnerPortal"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const Admin = lazy(() => import("./pages/Admin"));
const DisputeForm = lazy(() => import("./pages/DisputeForm"));
const Pro = lazy(() => import("./pages/Pro"));
const Trust = lazy(() => import("./pages/Trust"));
const SystemChecks = lazy(() => import("./pages/SystemChecks"));
const QA = lazy(() => import("./pages/QA"));
const AdminFees = lazy(() => import("./pages/AdminFees"));
const AdminPrelaunch = lazy(() => import("./pages/AdminPrelaunch"));
const Terms = lazy(() => import("./pages/Terms"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Fees = lazy(() => import("./pages/Fees"));
const Disputes = lazy(() => import("./pages/Disputes"));
const Referrals = lazy(() => import("./pages/Referrals"));
const Collections = lazy(() => import("./pages/Collections"));
const Forums = lazy(() => import("./pages/Forums"));
const ForumCategory = lazy(() => import("./pages/ForumCategory"));
const ForumPost = lazy(() => import("./pages/ForumPost"));
const ReputationDashboard = lazy(() => import("./pages/ReputationDashboard"));
const AdminReputation = lazy(() => import("./pages/AdminReputation"));
const DataPrivacy = lazy(() => import("./pages/DataPrivacy"));
const LaunchReadiness = lazy(() => import("./pages/LaunchReadiness"));
const LaunchStrategy = lazy(() => import("./pages/LaunchStrategy"));
const PartnerDocs = lazy(() => import("./pages/PartnerDocs"));
const VaultPartners = lazy(() => import("./pages/VaultPartners"));
const VaultPartnerSetup = lazy(() => import("./pages/VaultPartnerSetup"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const BrandPortal = lazy(() => import("./pages/BrandPortal"));
const BrandPartners = lazy(() => import("./pages/BrandPartners"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const VaultPartnerDetail = lazy(() => import("./pages/VaultPartnerDetail"));
const Academy = lazy(() => import("./pages/Academy"));
const MarketAnalysis = lazy(() => import("./pages/MarketAnalysis"));
const BuyingGuides = lazy(() => import("./pages/BuyingGuides"));
const GradingStandards = lazy(() => import("./pages/GradingStandards"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Collectors = lazy(() => import("./pages/Collectors"));
const CollectorProfile = lazy(() => import("./pages/CollectorProfile"));
const Wallet = lazy(() => import("./pages/Wallet"));
const SellerEarnings = lazy(() => import("./pages/SellerEarnings"));
const SellerAnalytics = lazy(() => import("./pages/SellerAnalytics"));
const Install = lazy(() => import("./pages/Install"));
const UpgradePro = lazy(() => import("./pages/UpgradePro"));
const ProDashboard = lazy(() => import("./pages/pro/Dashboard"));
const BulkListing = lazy(() => import("./pages/pro/BulkListing"));
const BulkPricing = lazy(() => import("./pages/pro/BulkPricing"));
const VaultPortfolio = lazy(() => import("./pages/pro/VaultPortfolio"));
const OptionsAcademy = lazy(() => import("./pages/OptionsAcademy"));
const OptionsTrading = lazy(() => import("./pages/OptionsTrading"));
const TradingCards = lazy(() => import("./pages/TradingCards"));
const FoundingSeller = lazy(() => import("./pages/FoundingSeller"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CollectiblesPage = lazy(() => import("./pages/Collectibles"));
const FeatureComingSoon = lazy(() => import("./pages/FeatureComingSoon"));
const OrderHistory = lazy(() => import("./pages/OrderHistory"));
const NotificationsPage = lazy(() => import("./pages/Notifications"));
const SearchPage = lazy(() => import("./pages/Search"));
const AuthenticationQueue = lazy(() => import("./pages/admin/AuthenticationQueue"));
const CertificateViewer = lazy(() => import("./pages/verify/CertificateViewer"));
const IntegrationTest = lazy(() => import("./pages/IntegrationTest"));
const VaultPartnerDashboard = lazy(() => import("./pages/vault-partner/Dashboard"));
const IntakeQueue = lazy(() => import("./pages/vault-partner/IntakeQueue"));
const Inspection = lazy(() => import("./pages/vault-partner/Inspection"));
const Authentication = lazy(() => import("./pages/vault-partner/Authentication"));
const VaultInventory = lazy(() => import("./pages/vault-partner/Inventory"));
const Certificates = lazy(() => import("./pages/vault-partner/Certificates"));
const Fulfillment = lazy(() => import("./pages/vault-partner/Fulfillment"));
const ChainOfCustody = lazy(() => import("./pages/vault-partner/ChainOfCustody"));
const TeamAccess = lazy(() => import("./pages/vault-partner/TeamAccess"));
const Integrations = lazy(() => import("./pages/vault-partner/Integrations"));
const VaultPartnerSettings = lazy(() => import("./pages/vault-partner/VaultPartnerSettings"));
const BrandDashboard = lazy(() => import("./pages/brand/Dashboard"));
const BrandListings = lazy(() => import("./pages/brand/Listings"));
const BrandOrders = lazy(() => import("./pages/brand/Orders"));
const BrandAnalytics = lazy(() => import("./pages/brand/Analytics"));
const BrandInventory = lazy(() => import("./pages/brand/Inventory"));
const BrandSettingsPage = lazy(() => import("./pages/brand/Settings"));

const queryClient = new QueryClient();

const LANDING_ROUTES = new Set(["/", "/cinematic", "/prelaunch-cinematic", "/preview", "/market-signal", "/unsubscribe"]);

const AppFloatingWidgets = () => {
  const { pathname } = useLocation();
  if (LANDING_ROUTES.has(pathname)) return null;
  return (
    <>
      <AxisChatWidget />
      <SnapToPriceButton variant="fab" />
    </>
  );
};

const OnboardingWrapper = ({ children }: { children: React.ReactNode }) => {
  const { showAccountSetup, closeAccountSetup, completeAccountSetup } = useOnboarding();
  useDeviceFingerprint();
  return (
    <>
      {children}
      <AccountSetupWizard isOpen={showAccountSetup} onClose={closeAccountSetup} onComplete={completeAccountSetup} />
    </>
  );
};

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <TooltipProvider>
        <SubscriptionProvider>
          <OnboardingProvider>
            <ErrorBoundary>
              <OfflineIndicator />
              <AppUpdatePrompt />
              <SmartInstallPrompt />
              <Toaster />
              <Sonner />
              <CookieConsent />
              <OnboardingWrapper>
                <BrowserRouter>
                  <PendingPaymentProvider />
                  <AppFloatingWidgets />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<PreLaunch />} />
                      <Route path="/cinematic" element={<PreLaunchCinematic />} />
                      <Route path="/prelaunch-cinematic" element={<PreLaunchCinematic />} />
                      <Route path="/preview" element={<Index />} />
                      <Route path="/unsubscribe" element={<Unsubscribe />} />
                      <Route path="/market-signal" element={<MarketSignal />} />
                      <Route path="/markets" element={<Markets />} />
                      <Route path="/ticker/:symbol" element={<Ticker />} />
                      <Route path="/sell" element={<Sell />} />
                      <Route path="/collectibles" element={<CollectiblesPage />} />
                      <Route path="/trading-cards" element={<TradingCards />} />
                      <Route path="/founding-seller" element={<FoundingSeller />} />
                      <Route path="/portfolio" element={<Portfolio />} />
                      <Route path="/vault" element={<Vault />} />
                      <Route path="/vault/deposits" element={<VaultDeposits />} />
                      <Route path="/brand-portal" element={<BrandPortal />} />
                      <Route path="/brand-partners" element={<BrandPartners />} />
                      <Route path="/vault-partners" element={<VaultPartners />} />
                      <Route path="/vault-partner-setup" element={<VaultPartnerSetup />} />
                      <Route path="/partner/portal" element={<PartnerPortal />} />

                      <Route path="/brand" element={<BrandLayout />}>
                        <Route path="dashboard" element={<BrandDashboard />} />
                        <Route path="listings" element={<BrandListings />} />
                        <Route path="orders" element={<BrandOrders />} />
                        <Route path="analytics" element={<BrandAnalytics />} />
                        <Route path="inventory" element={<BrandInventory />} />
                        <Route path="settings" element={<BrandSettingsPage />} />
                      </Route>

                      <Route path="/vault-partner" element={<VaultPartnerLayout />}>
                        <Route path="dashboard" element={<VaultPartnerDashboard />} />
                        <Route path="intake" element={<IntakeQueue />} />
                        <Route path="inspection" element={<Inspection />} />
                        <Route path="authentication" element={<Authentication />} />
                        <Route path="inventory" element={<VaultInventory />} />
                        <Route path="certificates" element={<Certificates />} />
                        <Route path="fulfillment" element={<Fulfillment />} />
                        <Route path="chain-of-custody" element={<ChainOfCustody />} />
                        <Route path="team" element={<TeamAccess />} />
                        <Route path="integrations" element={<Integrations />} />
                        <Route path="settings" element={<VaultPartnerSettings />} />
                      </Route>

                      <Route path="/auth" element={<Auth />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path="/admin/portal" element={<AdminPortal />} />
                      <Route path="/admin/portal/*" element={<AdminPortal />} />
                      <Route path="/admin/vault-partner/:partnerId" element={<VaultPartnerDetail />} />
                      <Route path="/disputes/new" element={<DisputeForm />} />
                      <Route path="/pro" element={
                        <FeatureGate feature="pro_subscription" fallback={<FeatureComingSoon featureName="SwapX Pro" featureDescription="Our professional trading tier with lower fees, advanced tools, and AI assistance is coming soon!" featureKey="pro_subscription" />}>
                          <Pro />
                        </FeatureGate>
                      } />
                      <Route path="/pro/dashboard" element={
                        <FeatureGate feature="pro_dashboard" fallback={<FeatureComingSoon featureName="Pro Dashboard" featureDescription="Advanced trading terminal with customizable widgets coming soon!" />}>
                          <ProDashboard />
                        </FeatureGate>
                      } />
                      <Route path="/pro/bulk-listing" element={
                        <FeatureGate feature="pro_bulk_tools" fallback={<FeatureComingSoon featureName="Bulk Listing" featureDescription="Bulk listing tools for power sellers coming soon!" />}>
                          <BulkListing />
                        </FeatureGate>
                      } />
                      <Route path="/pro/bulk-pricing" element={
                        <FeatureGate feature="pro_bulk_tools" fallback={<FeatureComingSoon featureName="Bulk Pricing" featureDescription="Bulk pricing tools for power sellers coming soon!" />}>
                          <BulkPricing />
                        </FeatureGate>
                      } />
                      <Route path="/pro/vault-portfolio" element={<VaultPortfolio />} />
                      <Route path="/pro/options-academy" element={
                        <FeatureGate feature="pro_options" fallback={<FeatureComingSoon featureName="Options Academy" featureDescription="Learn options trading with our comprehensive academy - coming soon!" />}>
                          <OptionsAcademy />
                        </FeatureGate>
                      } />
                      <Route path="/pro/options-trading" element={
                        <FeatureGate feature="pro_options" fallback={<FeatureComingSoon featureName="Options Trading" featureDescription="Trade options contracts on collectibles - coming soon!" />}>
                          <OptionsTrading />
                        </FeatureGate>
                      } />
                      <Route path="/upgrade/pro" element={<UpgradePro />} />
                      <Route path="/trust" element={<Trust />} />
                      <Route path="/system/checks" element={<SystemChecks />} />
                      <Route path="/qa" element={<QA />} />
                      <Route path="/admin/fees" element={<AdminFees />} />
                      <Route path="/admin/prelaunch" element={<AdminPrelaunch />} />
                      <Route path="/admin/reputation" element={<AdminReputation />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/shipping-policy" element={<ShippingPolicy />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/fees" element={<Fees />} />
                      <Route path="/disputes" element={<Disputes />} />
                      <Route path="/referrals" element={<Referrals />} />
                      <Route path="/seller-earnings" element={<SellerEarnings />} />
                      <Route path="/seller-analytics" element={<SellerAnalytics />} />
                      <Route path="/collections" element={<Collections />} />
                      <Route path="/forums" element={
                        <FeatureGate feature="community_forums" fallback={<FeatureComingSoon featureName="Forums" featureDescription="Community discussion forums coming soon!" />}>
                          <Forums />
                        </FeatureGate>
                      } />
                      <Route path="/forums/:slug" element={
                        <FeatureGate feature="community_forums" fallback={<FeatureComingSoon featureName="Forums" featureDescription="Community discussion forums coming soon!" />}>
                          <ForumCategory />
                        </FeatureGate>
                      } />
                      <Route path="/forums/:slug/:postId" element={
                        <FeatureGate feature="community_forums" fallback={<FeatureComingSoon featureName="Forums" featureDescription="Community discussion forums coming soon!" />}>
                          <ForumPost />
                        </FeatureGate>
                      } />
                      <Route path="/reputation" element={<ReputationDashboard />} />
                      <Route path="/data-privacy" element={<DataPrivacy />} />
                      <Route path="/launch-readiness" element={<LaunchReadiness />} />
                      <Route path="/launch-strategy" element={<LaunchStrategy />} />
                      <Route path="/partner-docs" element={<PartnerDocs />} />
                      <Route path="/leaderboard" element={
                        <FeatureGate feature="community_reputation" fallback={<FeatureComingSoon featureName="Leaderboard" featureDescription="Top traders leaderboard coming soon!" />}>
                          <Leaderboard />
                        </FeatureGate>
                      } />
                      <Route path="/user/:userId" element={<PublicProfile />} />
                      <Route path="/collectors" element={
                        <FeatureGate feature="community_reputation" fallback={<FeatureComingSoon featureName="Collectors" featureDescription="Browse and follow top collectors - coming soon!" />}>
                          <Collectors />
                        </FeatureGate>
                      } />
                      <Route path="/profile/:userId" element={<CollectorProfile />} />
                      <Route path="/academy" element={<Academy />} />
                      <Route path="/market-analysis" element={<MarketAnalysis />} />
                      <Route path="/buying-guides" element={<BuyingGuides />} />
                      <Route path="/grading-standards" element={<GradingStandards />} />
                      <Route path="/help" element={<HelpCenter />} />
                      <Route path="/payments" element={<Wallet />} />
                      <Route path="/install" element={<Install />} />
                      <Route path="/order-history" element={<OrderHistory />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/search" element={<SearchPage />} />
                      <Route path="/integration-test" element={<IntegrationTest />} />
                      <Route path="/admin/authentication-queue" element={<AuthenticationQueue />} />
                      <Route path="/verify/:id" element={<CertificateViewer />} />
                      <Route path="/verify" element={<CertificateViewer />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  <MobileBottomNav />
                </BrowserRouter>
              </OnboardingWrapper>
            </ErrorBoundary>
          </OnboardingProvider>
        </SubscriptionProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
